import { Component, Signal, computed, input } from "@angular/core";
import { CommonModule, NgClass } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";

import { VaultItemGroup } from "@bitwarden/web-vault/app/services/dali/vault-explorer.service";
import { VaultCodeEnum } from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { VaultDataTypeBase } from "../vault-data-type-base";
import { GlossScrollDirective } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll.directive";

@Component({
  selector: "app-vault-type-generic",
  standalone: true,
  imports: [GlossScrollDirective, CommonModule, NgClass, MatCheckboxModule, MatButtonModule],
  templateUrl: "./vault-type-generic.component.html",
})
export class VaultTypeGenericComponent extends VaultDataTypeBase {
  readonly vaultType = input.required<VaultCodeEnum>();
  protected readonly _vaultType: Signal<VaultCodeEnum> = this.vaultType;

  private readonly categoryDedupTypes: VaultCodeEnum[] = [
    VaultCodeEnum.Category,
    VaultCodeEnum.SourceCategory,
  ];

  private usesCategoryNameDedup(type: VaultCodeEnum): boolean {
    return this.categoryDedupTypes.includes(type);
  }

  /**
   * Categories / source categories dedupe by display name; other generic types use standard hashing.
   */
  override readonly vaultItemGroups = computed((): VaultItemGroup[] => {
    const type = this._vaultType();
    if (!type) {
      return [];
    }
    const items = this.vaultExplorer.getItemsByType(type);
    if (this.usesCategoryNameDedup(type)) {
      return this.vaultExplorer.groupItemsByCategoryDisplayName(items, type);
    }
    return this.duplicatesService.getGroupedItems(type, this.vaultIncludeIdForDedup());
  });

  override async removeAllDuplicates(): Promise<void> {
    const type = this._vaultType();
    if (!this.usesCategoryNameDedup(type)) {
      await super.removeAllDuplicates();
      return;
    }
    if (this.vaultDuplicateGroups().length === 0) {
      return;
    }
    const groups = this.vaultDuplicateGroups().length;
    const dupRecords = this.vaultTotalDuplicateRecordsToRemove();
    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.removeAllVaultDuplicatesForType,
      {
        messageReplacement: `Remove ${dupRecords} duplicate record(s) across ${groups} group(s)? Rows with the same category name are duplicates; the newest copy per name (by date created) is kept.`,
      },
    );
    if (!confirmed) {
      return;
    }

    try {
      const n = await this.vaultExplorer.deleteAllDuplicatesForVaultCategoryDisplayName(type);
      await this.vaultExplorer.reloadVaultDataForType(type);
      this.confirmationService.notify(
        NotificationEnum.success,
        "Duplicates removed",
        `${n} duplicate record(s) removed.`,
        false,
      );
    } catch {
      this.confirmationService.notify(
        NotificationEnum.error,
        "error",
        "Failed to remove duplicates.",
        false,
      );
    }
  }
}
